import { UserMenu } from '@/components/auth/user-menu';
import { GcpConnectionSetup } from '@/components/gcp-connection-setup';
import { GoogleCloudConnectionBanner } from '@/components/google-cloud-connection-banner';
import { SettingsModal } from '@/components/settings-modal';
import { Button } from '@/components/ui';
import { ConnectionSelectorButton } from '@/features/connections/connection-selector-button';
import { ChevronDown, RefreshCw, Settings, TextSearch, X } from 'lucide-react';
import { Breadcrumb } from '../../components/breadcrumb';
import { DocumentInspector } from '../../components/document-inspector';
import { DocumentTable } from '../../components/document-table';
import { JsonEditorModal } from '../../components/json-editor-modal';
import { SearchFilterBar } from '../../components/search-filter-bar';
import { Sidebar } from '../../components/sidebar';
import { QueryBuilderPanel } from '../../query-builder/query-builder-panel';
import { QueryBuilderProvider } from '../../query-builder/query-builder-store';
import { CreditsBalanceBadge } from './credits-balance-badge';
import { useExplorer } from './use-explorer';

function ExplorerBrandHeading() {
  return (
    <h1 className="text-xl font-semibold text-foreground m-0 flex items-center gap-2">
      <img
        src="/img/emberdesk-1024-full-tw.png"
        alt=""
        width={32}
        height={32}
        className="h-8 w-8 shrink-0 object-contain"
        aria-hidden
      />
      <span>emberdesk</span>
    </h1>
  );
}

export function Explorer() {
  return (
    <QueryBuilderProvider>
      <ExplorerContent />
    </QueryBuilderProvider>
  );
}

function ExplorerContent() {
  const f = useExplorer();

  if (f.showConnectionSetup) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="bg-card shadow-sm border-b">
          <div className="px-4">
            <div className="flex justify-between items-center h-16">
              <ExplorerBrandHeading />
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => f.setSettingsOpen(true)}
                  aria-label="Settings"
                >
                  <Settings className="h-5 w-5" />
                </Button>
                <UserMenu />
              </div>
            </div>
          </div>
        </header>
        <div className="flex-1 p-6">
          <GcpConnectionSetup
            oauthStatus={f.oauthStatus}
            onConnectionComplete={f.refreshOAuthStatus}
          />
        </div>
        <SettingsModal open={f.settingsOpen} onOpenChange={f.setSettingsOpen} />
      </div>
    );
  }

  if (f.showConnectionBannerOnly) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="bg-card shadow-sm border-b">
          <div className="px-4">
            <div className="flex justify-between items-center h-16">
              <ExplorerBrandHeading />
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => f.setSettingsOpen(true)}
                  aria-label="Settings"
                >
                  <Settings className="h-5 w-5" />
                </Button>
                <UserMenu />
              </div>
            </div>
          </div>
        </header>
        <div className="flex-1 p-6">
          {f.oauthStatus && f.oauthStatus.status === 'disconnected' && (
            <GoogleCloudConnectionBanner
              oauthStatus={f.oauthStatus}
              onStatusChange={f.refreshOAuthStatus}
            />
          )}
        </div>
        <SettingsModal open={f.settingsOpen} onOpenChange={f.setSettingsOpen} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="bg-card shadow-sm border-b">
        <div className="px-4">
          <div className="flex justify-between items-center h-16">
            <ExplorerBrandHeading />
            <div className="flex items-center gap-2">
              <CreditsBalanceBadge />
              <ConnectionSelectorButton />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => f.setSettingsOpen(true)}
                aria-label="Settings"
              >
                <Settings className="h-5 w-5" />
              </Button>
              <UserMenu />
            </div>
          </div>
        </div>
      </header>
      <div className="flex-1 flex overflow-hidden">
        <div className="w-64 flex-shrink-0">
          <Sidebar
            selectedCollection={f.collectionName}
            onSelectCollection={f.handleSelectCollection}
          />
        </div>
        <div className="flex-1 flex flex-col overflow-hidden">
          {f.oauthStatus && f.oauthStatus.status === 'disconnected' && (
            <div className="p-4 border-b">
              <GoogleCloudConnectionBanner
                oauthStatus={f.oauthStatus}
                onStatusChange={f.refreshOAuthStatus}
              />
            </div>
          )}
          {f.openTabIds.length > 0 && (
            <div className="shrink-0">
              <div className="grid grid-cols-1 sm:hidden px-4 pt-2">
                <select
                  value={f.collectionName ?? f.openTabIds[0] ?? ''}
                  onChange={(e) => f.handleSwitchTab(e.target.value)}
                  aria-label="Select a tab"
                  className="col-start-1 row-start-1 w-full cursor-pointer appearance-none rounded-md border border-input bg-background py-2 pr-8 pl-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  {f.openTabIds.map((tabId) => {
                    const label = tabId.includes('/') ? (tabId.split('/').pop() ?? tabId) : tabId;
                    return (
                      <option key={tabId} value={tabId}>
                        {label}
                      </option>
                    );
                  })}
                </select>
                <ChevronDown
                  aria-hidden
                  className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-muted-foreground"
                />
              </div>
              <div className="hidden sm:block border-b border-border">
                <nav aria-label="Tabs" className="-mb-px flex space-x-2 px-4">
                  {f.openTabIds.map((tabId) => {
                    const isActive = tabId === f.collectionName;
                    const label = tabId.includes('/') ? (tabId.split('/').pop() ?? tabId) : tabId;
                    return (
                      <div
                        key={tabId}
                        className={`
                          flex items-center gap-1 border-b-2 px-2 py-3 text-sm font-medium whitespace-nowrap
                          ${isActive ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'}
                        `}
                        onAuxClick={(e) => {
                          if (e.button === 1) {
                            e.preventDefault();
                            f.handleCloseTab(tabId);
                          }
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => f.handleSwitchTab(tabId)}
                          className="min-w-0 cursor-pointer truncate text-left"
                          title={tabId}
                          aria-current={isActive ? 'page' : undefined}
                        >
                          {label}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            f.handleCloseTab(tabId);
                          }}
                          className="shrink-0 cursor-pointer p-0.5 rounded hover:bg-muted"
                          aria-label={`Close ${label}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </nav>
              </div>
            </div>
          )}
          {f.showCollectionContent ? (
            <>
              <Breadcrumb
                collectionPath={f.collectionName!}
                onNavigate={f.handleBreadcrumbNavigate}
              />
              <div className="p-4 border-b flex items-center gap-4 justify-between">
                <Button
                  variant="outline"
                  size="icon"
                  title="Refresh data"
                  disabled={f.documentsFetching}
                  onClick={f.handleRefresh}
                  className="shrink-0"
                >
                  <RefreshCw className={`h-4 w-4 ${f.documentsFetching ? 'animate-spin' : ''}`} />
                </Button>
                <div className="flex items-center gap-4 justify-end">
                  <SearchFilterBar searchQuery={f.searchQuery} onSearchChange={f.setSearchQuery} />
                  <Button
                    variant="outline"
                    onClick={() => f.setQueryBuilderOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <TextSearch className="h-4 w-4" />
                    Query Builder
                  </Button>
                </div>
              </div>
              {f.queryResults && (
                <div className="px-4 py-2 bg-muted/50 border-b flex items-center justify-between text-sm">
                  <span>
                    Query Results: {f.queryResults.documents.length} documents (
                    {f.queryResults.meta.executionMs} ms, {f.queryResults.meta.readCount} reads)
                  </span>
                  <Button variant="ghost" size="sm" onClick={f.handleClearQuery}>
                    Clear Query
                  </Button>
                </div>
              )}
              {f.displayError && (
                <div className="p-4 bg-destructive/10 text-destructive text-sm">
                  {f.displayError}
                </div>
              )}
              <div className="flex-1 overflow-hidden">
                <DocumentTable
                  documents={f.displayDocuments}
                  loading={f.isLoading}
                  loadingMore={f.documentsLoadingMore}
                  hasMore={f.hasMore}
                  onLoadMore={f.documentsLoadMore}
                  onRowClick={f.handleRowClick}
                  sortState={f.tableSortState}
                  onSortChange={f.handleSortChange}
                  filters={f.activeQuery?.filters}
                  orFilters={f.activeQuery?.orGroups?.[0]}
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <h2 className="text-2xl font-semibold mb-2">Select a Collection</h2>
                <p>Choose a collection from the sidebar to view documents</p>
              </div>
            </div>
          )}
        </div>
      </div>
      <QueryBuilderPanel
        open={f.queryBuilderOpen}
        onOpenChange={f.setQueryBuilderOpen}
        collectionName={f.collectionName || ''}
        availableFields={f.availableFields}
        onClearQuery={f.handleClearQuery}
      />
      <DocumentInspector
        document={f.selectedDocument}
        open={f.inspectorOpen}
        onOpenChange={f.handleCloseInspector}
        onViewJson={f.handleViewJson}
      />
      <JsonEditorModal
        document={f.selectedDocument}
        open={f.editorOpen}
        onOpenChange={f.setEditorOpen}
      />
      <SettingsModal open={f.settingsOpen} onOpenChange={f.setSettingsOpen} />
    </div>
  );
}
